import { useCallback, useEffect, useState } from "react";
import { Pause, Play, MessageCircleQuestion, Square, RotateCcw, Clock, Loader2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { liveApi, errMsg } from "@/lib/simranLive";
import { useClassRole } from "@/lib/access";
import { LiveLocked } from "@/components/simran/LiveLocked";
import { useLiveLecture } from "@/components/simran/useLiveLecture";
import { TopicPicker } from "@/components/simran/TopicPicker";
import { Steps, Portrait, Caption, SectionCard } from "@/components/simran/LiveStage";
import { CheckpointCard } from "@/components/simran/CheckpointCard";
import { AskPanel } from "@/components/simran/AskPanel";
import { LiveDone } from "@/components/simran/LiveDone";

const GREET = {
  hi: (t) => `नमस्ते! मैं Simran Ma'am हूँ। आज हम ${t} सीखेंगे। तीन हिस्सों में समझाऊँगी, और हर हिस्से के बाद एक छोटा सा checkpoint सवाल आएगा। बीच में कुछ भी पूछना हो, तो Pause and Ask दबाइए। चलिए, शुरू करते हैं।`,
  en: (t) => `Namaste! I'm Simran Ma'am. Today we will learn ${t}. I will explain it in three parts, with a short checkpoint question after each part. If you have a doubt at any time, just press Pause and Ask. Let's begin.`,
};
const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

const Controls = ({ live, asking, onAsk, onEnd }) => {
  const { L } = useLang();
  const btn = "h-11 px-4 text-sm sm:h-12 sm:px-5 sm:text-base rounded-full font-black inline-flex items-center gap-2 transition-colors disabled:opacity-40";
  return (
    <div className="flex sm:flex-wrap items-center gap-2 sticky bottom-0 z-10 -mx-5 px-5 py-3 bg-[#0B1F3A]/95 backdrop-blur sm:static sm:mx-0 sm:p-0 sm:bg-transparent sm:backdrop-blur-none" data-testid="live-controls">
      <button type="button" onClick={live.paused ? live.resume : live.pause} disabled={asking} data-testid="live-play-btn" className={`${btn} bg-white text-[#0B1F3A] hover:bg-[#CCFBF1]`}>
        {live.paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}{live.paused ? L("Resume", "जारी रखें") : L("Pause", "रोकें")}
      </button>
      <button type="button" onClick={onAsk} disabled={asking} data-testid="live-ask-btn" className={`${btn} bg-[#F59E0B] text-[#0B1F3A] hover:bg-[#FBBF24]`}>
        <MessageCircleQuestion className="w-4 h-4" />{L("Pause & Ask", "रोकें और पूछें")}
      </button>
      <button type="button" onClick={onEnd} aria-label={L("End", "बंद करें")} data-testid="live-end-btn" className={`${btn} border border-white/20 text-slate-300 hover:bg-white/10 ml-auto shrink-0`}><Square className="w-4 h-4" /><span className="hidden sm:inline">{L("End", "बंद करें")}</span></button>
    </div>
  );
};

const Stage = ({ live, secs, asking, setAsking }) => {
  const { L } = useLang();
  const resumeAfterAsk = () => { setAsking(false); live.resume(); };
  return (
    <section className="rounded-[2rem] bg-[#0B1F3A] text-white p-5 sm:p-8" data-testid="live-stage">
      <div className="flex flex-wrap items-center gap-3">
        <span className="inline-flex items-center gap-2 rounded-full bg-[#FF3B3B] px-3 py-1 text-[11px] font-black tracking-[0.2em]"><span className="w-2 h-2 rounded-full bg-white animate-pulse" />LIVE</span>
        <h1 className="text-base sm:text-lg font-extrabold min-w-0 flex-1 truncate" data-testid="live-title">{live.lec?.title}</h1>
        <span className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-300" data-testid="live-timer"><Clock className="w-4 h-4" />{mmss(secs)} / ~13:00</span>
      </div>
      <div className="mt-4"><Steps n={live.n} /></div>
      <div className="mt-6 grid lg:grid-cols-[260px_1fr] gap-6 lg:gap-10 items-start">
        <div className="max-w-[130px] sm:max-w-[220px] lg:max-w-none"><Portrait speaking={live.speaking} writing={live.writing} /></div>
        <div className="min-w-0 space-y-6">
          {live.phase === "check" && live.check ? <CheckpointCard check={live.check} onPick={live.answer} onNext={live.next} /> : <SectionCard sec={live.sec} />}
          {live.phase !== "check" && <Caption cap={live.cap} t={live.t} />}
          {live.error && (
            <div className="rounded-2xl bg-[#FF3B3B]/10 border border-[#FF6B6B]/40 p-4 flex flex-wrap items-center gap-3" data-testid="live-error">
              <span className="text-sm text-[#FFB4B4] flex-1">{live.error === "failed" ? L("Simran Ma'am lost the connection for a moment.", "Simran Ma'am का कनेक्शन एक पल के लिए टूट गया।") : live.error}</span>
              <button type="button" onClick={live.retry} data-testid="live-retry-btn" className="h-10 px-4 rounded-full bg-white text-[#0B1F3A] font-bold inline-flex items-center gap-2"><RotateCcw className="w-4 h-4" />{L("Retry", "फिर कोशिश करें")}</button>
            </div>
          )}
          {asking ? <AskPanel lec={live.lec} section={live.n} onResume={resumeAfterAsk} /> : <Controls live={live} asking={asking} onAsk={() => { live.pause(); setAsking(true); }} onEnd={live.stop} />}
        </div>
      </div>
    </section>
  );
};

export default function SimranLive() {
  const role = useClassRole();
  if (role === null) return <div className="min-h-screen bg-[#0B1F3A] grid place-items-center" data-testid="live-role-loading"><Loader2 className="w-7 h-7 animate-spin text-[#5EEAD4]" /></div>;
  return role === "enrolled" ? <LiveClassroom /> : <LiveLocked role={role} />;
}

function LiveClassroom() {
  const { L } = useLang();
  const [lang, setLang] = useState("hi");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [secs, setSecs] = useState(0);
  const [asking, setAsking] = useState(false);
  const greet = useCallback((lec) => GREET[lec.lang](lec.topic), []);
  const live = useLiveLecture(lang, greet);
  const running = (live.phase === "live" || live.phase === "check") && !live.paused;
  useEffect(() => { if (!running) return undefined; const id = setInterval(() => setSecs((s) => s + 1), 1000); return () => clearInterval(id); }, [running]);
  useEffect(() => { document.title = "Simran Live · RoboSpark"; }, []);
  const start = async (topic) => {
    setBusy(true); setErr("");
    try { const lec = await liveApi.start(topic, lang); setSecs(0); setAsking(false); live.start(lec); window.scrollTo({ top: 0, behavior: "smooth" }); }
    catch (e) { setErr(errMsg(e, L("Simran Ma'am could not start right now. Please try again.", "Simran Ma'am अभी शुरू नहीं कर पाईं। फिर कोशिश करें।"))); }
    finally { setBusy(false); }
  };
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid="simran-live-page">
      {live.phase === "idle" && <TopicPicker lang={lang} setLang={setLang} onStart={start} busy={busy} error={err} />}
      {(live.phase === "live" || live.phase === "check") && <Stage live={live} secs={secs} asking={asking} setAsking={setAsking} />}
      {live.phase === "done" && <LiveDone lec={live.lec} secs={secs} asked={live.lec?.asked ?? 0} onReplay={() => { setSecs(0); live.replay(); }} onNew={live.stop} />}
      <p className="mt-6 text-xs text-slate-500 max-w-3xl" data-testid="live-disclaimer">{L("Simran Ma'am is an AI teacher. Lectures are written live by AI, so double-check important facts with your trainer.", "Simran Ma'am एक AI टीचर हैं। लेक्चर AI लाइव लिखता है, इसलिए ज़रूरी बातें अपने ट्रेनर से ज़रूर पक्की करें।")}</p>
    </div>
  );
}
